<script setup lang="ts">
import { Head, Link, useForm } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import StockLegend from '@/Pages/Kasir/Stocks/StockLegend.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Dialog, InputNumber, Select, Textarea } from 'primevue'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'
import { faClipboardCheck, faExchange, faIndustry, faPlusCircle, faSignInAlt, faSignOutAlt, faUndoAlt } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'

library.add(faClipboardCheck, faExchange, faIndustry, faPlusCircle, faSignInAlt, faSignOutAlt, faUndoAlt)

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    data: {
        id: number
        product_name: string
        product_slug: string | null
        sku: string | null
        print_type: string | null
        print_type_label: string | null
        print_type_icon: { tooltip: string, icon: string, class: string } | null
        is_general: boolean
        brand_name: string | null
        brand_slug: string | null
        on_hand: number
        reserved: number
        available: number
        can_manufacture: boolean
        is_archived: boolean
    }
    reservations: {
        id: number
        quantity: number
        order_slug: string | null
        reference: string | null
        brand_name: string | null
        created_at: string | null
    }[]
    manufactures: {
        id: number
        slug: string
        reference: string
        quantity: number
        finishing_count: number | null
        state_label: string
        state_icon: { tooltip: string, icon: string, class: string }
        created_at: string
    }[]
    movements: {
        id: number
        type_label: string
        type_icon: { tooltip: string, icon: string, class: string }
        quantity: number
        balance_after: number
        note: string | null
        manufacture_slug: string | null
        related_stock_id: number | null
        creator_name: string | null
        created_at: string
    }[]
    movementLimit: number
    transferTargets: {
        brand_product_id: number
        brand_name: string | null
        customer_name: string | null
        stock_on_hand: number | null
    }[]
    routes: Record<string, { name: string, parameters?: any[] }>
}>()

const storeMovementUrl = computed(() => route(props.routes.store_movement.name, props.routes.store_movement.parameters))

const opnameForm = useForm({
    quantity: null as number | null,
    note: '',
})

const opnameDifference = computed({
    get: () => opnameForm.quantity === null ? null : opnameForm.quantity - props.data.on_hand,
    set: (difference: number | null) => {
        opnameForm.quantity = difference === null ? null : props.data.on_hand + difference
        opnameForm.clearErrors('quantity')
    },
})

const submitOpname = () => {
    opnameForm.post(storeMovementUrl.value, {
        preserveScroll: true,
        onSuccess: () => opnameForm.reset(),
    })
}

const isManufactureModalOpen = ref(false)

const manufactureForm = useForm({
    quantity: null as number | null,
    note: '',
})

const submitManufacture = () => {
    manufactureForm.post(route(props.routes.store_manufacture.name, props.routes.store_manufacture.parameters), {
        preserveScroll: true,
        onSuccess: () => {
            manufactureForm.reset()
            isManufactureModalOpen.value = false
        },
    })
}

const isTransferModalOpen = ref(false)

const transferForm = useForm({
    target_brand_product_id: null as number | null,
    quantity: null as number | null,
    note: '',
})

const selectedTransferTarget = computed(() =>
    props.transferTargets.find(target => target.brand_product_id === transferForm.target_brand_product_id)
)

const transferPreview = computed(() => {
    const quantity = transferForm.quantity ?? 0
    if (!selectedTransferTarget.value || quantity < 1) return null

    const targetOnHandBefore = selectedTransferTarget.value.stock_on_hand ?? 0

    return {
        sourceOnHand: props.data.on_hand - quantity,
        sourceAvailable: props.data.available - quantity,
        targetOnHandBefore,
        targetOnHand: targetOnHandBefore + quantity,
    }
})

const submitTransfer = () => {
    transferForm.post(route(props.routes.store_transfer.name, props.routes.store_transfer.parameters), {
        preserveScroll: true,
        onSuccess: () => {
            transferForm.reset()
            isTransferModalOpen.value = false
        },
    })
}

const formatSigned =(value: number) => (value > 0 ? '+' : '') + useFormatNumber(value)
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="grid gap-4 px-4 py-4 lg:grid-cols-3">
        <div class="flex flex-col gap-4">
            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-4">
                    <Link v-if="data.product_slug" :href="route('kasir.products.show', data.product_slug)" class="text-lg font-bold primaryLink">
                        {{ data.product_name }}
                    </Link>
                    <div v-else class="text-lg font-bold">{{ data.product_name }}</div>
                    <div class="font-mono text-xs text-gray-400">{{ data.sku }}</div>
                    <div
                        v-if="data.print_type_label"
                        class="mt-1 inline-flex items-center gap-x-1.5 text-sm text-gray-600"
                        v-tooltip="data.print_type === 'ditakurnia'
                            ? 'Dicetak di Dita Kurnia: stok bisa ditambah lewat produksi di Manufaktur'
                            : 'Dicetak di luar: stok ditambah lewat stok opname'"
                    >
                        <Icon v-if="data.print_type_icon" :data="data.print_type_icon" />
                        {{ data.print_type_label }}
                    </div>
                    <div class="mt-2">
                        <Tag v-if="data.is_general" label="Stok umum (semua brand)" :theme="4" noHoverColor />
                        <div v-else-if="data.brand_slug">
                            Stok brand: <Link :href="route('kasir.brands.show', data.brand_slug)" class="text-sm secondaryLink">
                                {{ data.brand_name }}
                            </Link>
                        </div>
                    </div>
                </div>
                <div class="px-6 py-5 text-center">
                    <div class="text-xs uppercase tracking-wide text-gray-400" v-tooltip="'Stok fisik dikurangi reserved: yang masih bisa dipesan'">
                        Stok tersedia
                        <FontAwesomeIcon icon="fal fa-info-circle" fixed-width aria-hidden="true" />
                    </div>
                    <div class="text-3xl font-bold" :class="data.available > 0 ? 'text-gray-800' : 'text-red-500'">
                        {{ useFormatNumber(data.available) }}
                        <span class="text-base font-normal text-gray-400">pcs</span>
                    </div>
                    <div class="mt-3 grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100 pt-3 text-sm">
                        <div v-tooltip="'Barang yang benar-benar ada di gudang'">
                            <div class="text-xs text-gray-400">
                                Fisik
                                <FontAwesomeIcon icon="fal fa-info-circle" fixed-width aria-hidden="true" />
                            </div>
                            <div class="font-semibold">{{ useFormatNumber(data.on_hand) }}</div>
                        </div>
                        <div v-tooltip="'Sudah dipesan tapi belum dikirim, masih ada di gudang'">
                            <div class="text-xs text-gray-400">
                                Reserved
                                <FontAwesomeIcon icon="fal fa-info-circle" fixed-width aria-hidden="true" />
                            </div>
                            <div class="font-semibold" :class="data.reserved > 0 ? 'text-amber-600' : ''">{{ useFormatNumber(data.reserved) }}</div>
                        </div>
                    </div>
                    <div class="mt-3 border-t border-gray-100 pt-3 text-left">
                        <StockLegend compact />
                    </div>
                </div>
            </section>

            <section v-if="data.is_archived" class="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                Stok ini <span class="font-semibold">nonaktif</span>. Produknya kembali diproduksi per pesanan.
                Aktifkan lagi lewat tombol "Pakai sistem stok" di halaman
                <Link v-if="data.brand_slug" :href="route('kasir.brands.show', data.brand_slug)" class="secondaryLink">brand {{ data.brand_name }}</Link>.
            </section>

            <section v-else class="rounded-xl border border-gray-200 p-4">
                <div class="font-semibold text-gray-700">Stok opname</div>
                <p class="mb-3 text-xs text-gray-400">
                    Isi stok baru atau selisihnya, keduanya saling menyesuaikan.
                    Stok saat ini {{ useFormatNumber(data.on_hand) }} pcs.
                </p>
                <div class="flex flex-col gap-3">
                    <div class="grid grid-cols-2 gap-2">
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Stok baru</label>
                            <InputNumber
                                v-model="opnameForm.quantity"
                                :min="0"
                                locale="id-ID"
                                placeholder="0"
                                suffix=" pcs"
                                size="small"
                                fluid
                                :class="opnameForm.errors.quantity ? 'errorShake' : ''"
                                :invalid="!!opnameForm.errors.quantity"
                                @update:modelValue="opnameForm.clearErrors('quantity')"
                            />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Selisih</label>
                            <InputNumber
                                v-model="opnameDifference"
                                :min="-data.on_hand"
                                locale="id-ID"
                                placeholder="0"
                                suffix=" pcs"
                                size="small"
                                fluid
                                :inputClass="opnameDifference === null || opnameDifference === 0 ? '' : opnameDifference > 0 ? '!text-emerald-600' : '!text-red-500'"
                            />
                        </div>
                    </div>
                    <div v-if="opnameForm.errors.quantity" class="-mt-2 text-xs text-red-500">{{ opnameForm.errors.quantity }}</div>

                    <div>
                        <label class="mb-1 block text-xs text-gray-500">Keterangan</label>
                        <Textarea v-model="opnameForm.note" rows="2" placeholder="Contoh: hitung manual" autoResize fluid class="text-sm" />
                    </div>

                    <Button full type="save" label="Simpan opname" icon="fal fa-clipboard-check" :loading="opnameForm.processing" :disabled="opnameForm.quantity === null" @click="submitOpname" />

                    <template v-if="data.can_manufacture">
                        <div class="flex items-center gap-x-2 text-xs text-gray-400">
                            <div class="h-px flex-1 bg-gray-200" />
                            atau
                            <div class="h-px flex-1 bg-gray-200" />
                        </div>
                        <Button full type="tertiary" label="Tambah stok via Manufaktur" icon="fal fa-industry" @click="isManufactureModalOpen = true" />
                    </template>
                </div>
            </section>

            <section v-if="!data.is_general && !data.is_archived" class="rounded-xl border border-gray-200 p-4">
                <div class="font-semibold text-gray-700">Transfer stok</div>
                <p class="mb-3 text-xs text-gray-400">
                    Pindahkan stok ke brand lain yang memakai produk dan variasi yang sama.
                </p>
                <div class="flex flex-col gap-2">
                    <Button
                        full
                        type="tertiary"
                        label="Transfer ke brand lain"
                        icon="fal fa-exchange"
                        :disabled="!transferTargets.length || data.available < 1"
                        @click="isTransferModalOpen = true"
                    />
                    <p v-if="!transferTargets.length" class="text-xs text-gray-400">
                        Belum ada brand lain yang punya produk ini. Tambahkan produk yang sama ke brand tujuan dulu.
                    </p>
                    <p v-else-if="data.available < 1" class="text-xs text-gray-400">Tidak ada stok tersedia untuk ditransfer.</p>
                </div>
            </section>
        </div>

        <div class="flex h-fit flex-col gap-4 lg:col-span-2">
            <section v-if="reservations.length" class="overflow-hidden rounded-xl border border-amber-300">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Reserved
                    <span class="text-xs font-normal text-gray-400">&middot; dipesan, stok fisik berkurang saat pengiriman pertama dicatat</span>
                </div>
                <table class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Pesanan</th>
                            <th class="px-6 py-2 text-left">Brand</th>
                            <th class="px-6 py-2 text-right">Jumlah</th>
                            <th class="px-6 py-2 text-left">Dibuat</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="reservation in reservations" :key="reservation.id">
                            <td class="px-6 py-3">
                                <Link v-if="reservation.order_slug" :href="route('kasir.orders.show', reservation.order_slug)" class="secondaryLink font-mono">
                                    {{ reservation.reference }}
                                </Link>
                            </td>
                            <td class="px-6 py-3">{{ reservation.brand_name || '-' }}</td>
                            <td class="px-6 py-3 text-right">{{ useFormatNumber(reservation.quantity) }} pcs</td>
                            <td class="px-6 py-3 text-xs text-gray-500">{{ useFormatTime(reservation.created_at ?? undefined, { formatTime: 'hm' }) }}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section v-if="manufactures.length" class="overflow-hidden rounded-xl border border-cyan-300">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Produksi berjalan
                    <span class="text-xs font-normal text-gray-400">&middot; hasilnya otomatis masuk ke stok ini</span>
                </div>
                <table class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Manufaktur</th>
                            <th class="px-6 py-2 text-right">Jumlah</th>
                            <th class="px-6 py-2 text-left">Status</th>
                            <th class="px-6 py-2 text-left">Dibuat</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="manufacture in manufactures" :key="manufacture.id">
                            <td class="px-6 py-3">
                                <Link :href="route('manufacture.show', manufacture.slug)" class="secondaryLink">
                                    {{ manufacture.reference }}
                                </Link>
                            </td>
                            <td class="px-6 py-3 text-right">
                                {{ useFormatNumber(manufacture.quantity) }} pcs
                                <div v-if="manufacture.finishing_count !== null" class="text-xs text-gray-400">
                                    Finishing: {{ useFormatNumber(manufacture.finishing_count) }}
                                </div>
                            </td>
                            <td class="px-6 py-3">
                                <div class="flex items-center gap-x-1.5">
                                    <Icon :data="manufacture.state_icon" />
                                    {{ manufacture.state_label }}
                                </div>
                            </td>
                            <td class="px-6 py-3 text-xs text-gray-500">{{ useFormatTime(manufacture.created_at, { formatTime: 'hm' }) }}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">Riwayat stok</div>

                <div v-if="!movements.length" class="px-6 py-6 text-center text-sm text-gray-400">
                    Belum ada pergerakan stok.
                </div>

                <table v-else class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Jenis</th>
                            <th class="px-6 py-2 text-right">Jumlah</th>
                            <th class="px-6 py-2 text-right">Stok setelah</th>
                            <th class="px-6 py-2 text-left">Waktu</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="movement in movements" :key="movement.id">
                            <td class="px-6 py-3">
                                <div class="flex items-center gap-x-2 font-medium">
                                    <Icon :data="movement.type_icon" />
                                    {{ movement.type_label }}
                                </div>
                                <Link v-if="movement.manufacture_slug" :href="route('manufacture.show', movement.manufacture_slug)" class="text-xs secondaryLink">
                                    {{ movement.note }}
                                </Link>
                                <Link v-else-if="movement.related_stock_id" :href="route('kasir.stocks.show', movement.related_stock_id)" class="text-xs secondaryLink">
                                    {{ movement.note }}
                                </Link>
                                <div v-else-if="movement.note" class="text-xs text-gray-500">{{ movement.note }}</div>
                            </td>
                            <td class="px-6 py-3 text-right font-medium"
                                :class="movement.quantity > 0 ? 'text-emerald-600' : movement.quantity < 0 ? 'text-red-500' : 'text-gray-500'">
                                {{ formatSigned(movement.quantity) }}
                            </td>
                            <td class="px-6 py-3 text-right">{{ useFormatNumber(movement.balance_after) }}</td>
                            <td class="px-6 py-3 text-xs text-gray-500">
                                {{ useFormatTime(movement.created_at, { formatTime: 'hm' }) }}
                                <div v-if="movement.creator_name">{{ movement.creator_name }}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-if="movements.length >= movementLimit" class="border-t border-gray-100 px-6 py-2 text-center text-xs text-gray-400">
                    Menampilkan {{ movementLimit }} riwayat terakhir.
                </div>
            </section>
        </div>
    </div>

    <Dialog
        v-model:visible="isManufactureModalOpen"
        modal
        header="Tambah stok via Manufaktur"
        :style="{ width: '28rem' }"
        :breakpoints="{ '640px': '92vw' }"
        @hide="manufactureForm.clearErrors()"
    >
        <p class="mb-4 text-sm text-gray-500">
            Produksi <span class="font-medium text-gray-700">{{ data.product_name }}</span> tanpa invoice.
            Hasil finishing (atau validasi admin) otomatis masuk ke stok ini.
        </p>
        <div class="flex flex-col gap-3">
            <div>
                <label class="mb-1 block text-xs text-gray-500">Jumlah produksi</label>
                <InputNumber
                    v-model="manufactureForm.quantity"
                    :min="1"
                    locale="id-ID"
                    placeholder="0"
                    suffix=" pcs"
                    size="small"
                    fluid
                    autofocus
                    :class="manufactureForm.errors.quantity ? 'errorShake' : ''"
                    :invalid="!!manufactureForm.errors.quantity"
                    @update:modelValue="manufactureForm.clearErrors('quantity')"
                />
                <div v-if="manufactureForm.errors.quantity" class="mt-1 text-xs text-red-500">{{ manufactureForm.errors.quantity }}</div>
            </div>
            <div>
                <label class="mb-1 block text-xs text-gray-500">Catatan untuk produksi</label>
                <Textarea v-model="manufactureForm.note" rows="2" autoResize fluid class="text-sm" />
            </div>
        </div>

        <template #footer>
            <Button type="tertiary" label="Batal" @click="isManufactureModalOpen = false" />
            <Button type="indigo" label="Buat produksi stok" icon="fal fa-industry" :loading="manufactureForm.processing" :disabled="!manufactureForm.quantity" @click="submitManufacture" />
        </template>
    </Dialog>

    <Dialog
        v-model:visible="isTransferModalOpen"
        modal
        header="Transfer stok ke brand lain"
        :style="{ width: '30rem' }"
        :breakpoints="{ '640px': '92vw' }"
        @hide="transferForm.clearErrors()"
    >
        <p class="mb-4 text-sm text-gray-500">
            Dari <span class="font-medium text-gray-700">{{ data.brand_name }}</span>,
            stok tersedia {{ useFormatNumber(data.available) }} pcs.
        </p>
        <div class="flex flex-col gap-3">
            <div>
                <label class="mb-1 block text-xs text-gray-500">Brand tujuan</label>
                <Select
                    v-model="transferForm.target_brand_product_id"
                    :options="transferTargets"
                    optionLabel="brand_name"
                    optionValue="brand_product_id"
                    placeholder="Pilih brand"
                    filter
                    size="small"
                    fluid
                    :invalid="!!transferForm.errors.target_brand_product_id"
                    @change="transferForm.clearErrors('target_brand_product_id')"
                >
                    <template #option="{ option }">
                        <div class="flex w-full items-center justify-between gap-x-3">
                            <div>
                                <div>{{ option.brand_name }}</div>
                                <div v-if="option.customer_name" class="text-xs text-gray-400">{{ option.customer_name }}</div>
                            </div>
                            <span class="shrink-0 text-xs text-gray-500">
                                {{ option.stock_on_hand === null ? 'Belum pakai stok' : `${useFormatNumber(option.stock_on_hand)} pcs` }}
                            </span>
                        </div>
                    </template>
                </Select>
                <div v-if="transferForm.errors.target_brand_product_id" class="mt-1 text-xs text-red-500">{{ transferForm.errors.target_brand_product_id }}</div>
                <div v-else-if="selectedTransferTarget && selectedTransferTarget.stock_on_hand === null" class="mt-1 text-xs text-amber-600">
                    Produk ini di brand {{ selectedTransferTarget.brand_name }} akan otomatis diubah menjadi sistem stok.
                </div>
            </div>
            <div>
                <label class="mb-1 block text-xs text-gray-500">Jumlah transfer</label>
                <InputNumber
                    v-model="transferForm.quantity"
                    :min="1"
                    :max="data.available"
                    locale="id-ID"
                    placeholder="0"
                    suffix=" pcs"
                    size="small"
                    fluid
                    :class="transferForm.errors.quantity ? 'errorShake' : ''"
                    :invalid="!!transferForm.errors.quantity"
                    @update:modelValue="transferForm.clearErrors('quantity')"
                />
                <div v-if="transferForm.errors.quantity" class="mt-1 text-xs text-red-500">{{ transferForm.errors.quantity }}</div>
            </div>

            <div
                v-if="selectedTransferTarget && transferPreview"
                class="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm"
                v-tooltip="'Perkiraan stok setelah transfer disimpan'"
            >
                <div>
                    <div class="text-xs uppercase tracking-wide text-gray-400">Asal &middot; {{ data.brand_name }}</div>
                    <div class="mt-1">
                        Fisik:
                        <span class="text-gray-400">{{ useFormatNumber(data.on_hand) }}</span>
                        &rarr;
                        <span class="font-semibold text-red-500">{{ useFormatNumber(transferPreview.sourceOnHand) }}</span>
                    </div>
                    <div>
                        Tersedia:
                        <span class="text-gray-400">{{ useFormatNumber(data.available) }}</span>
                        &rarr;
                        <span class="font-semibold" :class="transferPreview.sourceAvailable < 0 ? 'text-red-600' : 'text-red-500'">
                            {{ useFormatNumber(transferPreview.sourceAvailable) }}
                        </span>
                    </div>
                </div>
                <div>
                    <div class="text-xs uppercase tracking-wide text-gray-400">Tujuan &middot; {{ selectedTransferTarget.brand_name }}</div>
                    <div class="mt-1">
                        Fisik:
                        <span class="text-gray-400">{{ useFormatNumber(transferPreview.targetOnHandBefore) }}</span>
                        &rarr;
                        <span class="font-semibold text-emerald-600">{{ useFormatNumber(transferPreview.targetOnHand) }}</span>
                    </div>
                    <div v-if="selectedTransferTarget.stock_on_hand === null" class="text-xs text-amber-600">Stok baru dibuat otomatis</div>
                </div>
                <div v-if="transferPreview.sourceAvailable < 0" class="col-span-2 text-xs text-red-600">
                    Melebihi stok tersedia, transfer akan ditolak.
                </div>
            </div>

            <div>
                <label class="mb-1 block text-xs text-gray-500">Keterangan</label>
                <Textarea v-model="transferForm.note" rows="2" placeholder="Contoh: permintaan Lambao untuk pesanan minggu ini" autoResize fluid class="text-sm" />
            </div>
        </div>

        <template #footer>
            <Button type="tertiary" label="Batal" @click="isTransferModalOpen = false" />
            <Button
                type="save"
                label="Transfer stok"
                icon="fal fa-exchange"
                :loading="transferForm.processing"
                :disabled="!transferForm.target_brand_product_id || !transferForm.quantity"
                @click="submitTransfer"
            />
        </template>
    </Dialog>
</template>
